/**
 * Catalog vocabulary shared by course authoring and course search.
 *
 * These MUST stay a single list: a teacher picks a subject when creating a
 * course and a parent filters by the same value on the catalog. If the two
 * drifted, a newly created course would simply never appear under its own
 * subject filter.
 */

export const SUBJECTS = [
  'Matematicas',
  'Ciencias',
  'Ingles',
  'Arte',
  'Musica',
  'Programacion',
  'Historia',
  'Lectura',
  'Escritura',
] as const;

export type Subject = (typeof SUBJECTS)[number];

/** Grade levels a course can target. Stored on the listing as `gradeLevel[]`. */
/**
 * The grade codes the platform stores, in the platform's own value space.
 *
 * These are codes, not labels. `IQMAX_GRADE_LEVELS` in
 * `backend/src/verticals/iqmax/application/listing-attributes.service.ts`
 * validates every course against exactly these strings, at create, at update
 * and at publish.
 *
 * This list used to hold `1ro` through `9no` — Spanish ordinals, nine of them,
 * sharing not one value with the eight the backend accepts. The authoring form
 * offered those labels as the stored value, so every course a teacher built
 * through the UI was refused at `createListing` with "Invalid grade level",
 * and the only courses on dev with a grade were the ones seed scripts wrote
 * directly. The label a parent reads is unchanged; what moved is what gets
 * sent.
 *
 * Kept in step by `taxonomy-parity.spec.ts`, which reads both files.
 */
export const GRADE_LEVELS = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th'] as const;

export type GradeLevel = (typeof GRADE_LEVELS)[number];

/**
 * How each grade reads in the interface.
 *
 * Presentation only, and separate from the code for the reason the two drifted
 * in the first place: a label is translated, a stored value is not. The ninth
 * grade this list used to offer is absent because the platform has no code for
 * it — a course in it could never be saved, so offering it was offering a
 * dead end. Whether IQMax teaches a ninth grade is a product question, and
 * adding a code is where it gets answered.
 */
export const GRADE_LEVEL_LABELS: Record<GradeLevel, string> = {
  '1st': '1ro',
  '2nd': '2do',
  '3rd': '3ro',
  '4th': '4to',
  '5th': '5to',
  '6th': '6to',
  '7th': '7mo',
  '8th': '8vo',
};

/** Backend CreateListingDto bounds — mirrored so the form fails before the request. */
export const COURSE_LIMITS = {
  titleMin: 3,
  titleMax: 200,
  descriptionMin: 10,
  durationMin: 15,
  durationMax: 180,
  maxStudentsMin: 1,
  maxStudentsMax: 30,
  priceMin: 0,
} as const;

// ── Course content (ADR-007 S4.3) ────────────────────────────────────────────

/** Mirrors SUPPORTED_COURSE_LANGUAGES in the kernel. */
export type CourseLanguage = 'es' | 'en';

export const COURSE_LANGUAGE_OPTIONS: Array<{ value: CourseLanguage; label: string }> = [
  { value: 'es', label: 'Español' },
  { value: 'en', label: 'English' },
];

/** Backend course-content-rules bounds — mirrored so the form fails before the request. */
export const CONTENT_LIMITS = {
  sectionMax: 2000,
  objectivesMax: 12,
  objectiveMax: 200,
} as const;

// ── Offer model (ADR-007 S4.2) ───────────────────────────────────────────────
//
// Mirrors the backend enums. The three dimensions are independent by design:
// what is sold, how it is delivered, how it is charged.

export type OfferMode = 'GROUP' | 'ONE_ON_ONE' | 'SUBSCRIPTION';
export type DeliveryFormat = 'LIVE' | 'ON_DEMAND';
export type BillingModel = 'PER_CYCLE' | 'RECURRING';
export type ScheduleType = 'RECURRING' | 'APPOINTMENT';
export type DurationMode = 'ONGOING' | 'FIXED';

export interface CourseSchedule {
  /** 0 = Sunday, matching the availability calendar. */
  classDays: number[];
  startTime: string;
  endTime: string;
  timezone: string;
}

/**
 * Modes a teacher can actually pick today.
 *
 * SUBSCRIPTION and ON_DEMAND exist in the model but are not admitted yet
 * (BC-02-E04, BC-12-E03). They are omitted from the form rather than shown
 * disabled — S11.6 asks for deferred capability to be hidden, not teased. The
 * server rejects them independently, so this list is convenience, not a control.
 */
export const OFFER_MODE_OPTIONS: Array<{ value: OfferMode; label: string; help: string }> = [
  {
    value: 'GROUP',
    label: 'Grupo',
    help: 'Varios alumnos en la misma clase.',
  },
  {
    value: 'ONE_ON_ONE',
    label: 'Individual',
    help: 'Un solo alumno por clase.',
  },
];

export const SCHEDULE_TYPE_OPTIONS: Array<{
  value: ScheduleType;
  label: string;
  help: string;
}> = [
  {
    value: 'APPOINTMENT',
    label: 'Por cita',
    help: 'Los padres agendan cada sesión según tu disponibilidad.',
  },
  {
    value: 'RECURRING',
    label: 'Horario fijo',
    help: 'La clase ocurre los mismos días y a la misma hora cada semana.',
  },
];

export const DURATION_MODE_OPTIONS: Array<{
  value: DurationMode;
  label: string;
  help: string;
}> = [
  { value: 'ONGOING', label: 'Continuo', help: 'Sin fecha de término definida.' },
  { value: 'FIXED', label: 'Duración fija', help: 'Termina después de un número de semanas.' },
];

/** 0 = Sunday, matching the backend and the availability screen. */
export const WEEK_DAYS = [
  { value: 1, label: 'Lun' },
  { value: 2, label: 'Mar' },
  { value: 3, label: 'Mié' },
  { value: 4, label: 'Jue' },
  { value: 5, label: 'Vie' },
  { value: 6, label: 'Sáb' },
  { value: 0, label: 'Dom' },
] as const;

const DAY_FULL: Record<number, string> = {
  0: 'Domingo',
  1: 'Lunes',
  2: 'Martes',
  3: 'Miércoles',
  4: 'Jueves',
  5: 'Viernes',
  6: 'Sábado',
};

/** "Martes y Jueves · 16:00–17:00" */
export function describeSchedule(schedule: CourseSchedule): string {
  const days = [...schedule.classDays].sort((a, b) => a - b).map((d) => DAY_FULL[d] ?? `Día ${d}`);
  const dayText =
    days.length <= 1 ? days[0] ?? '' : `${days.slice(0, -1).join(', ')} y ${days[days.length - 1]}`;
  return `${dayText} · ${schedule.startTime}–${schedule.endTime}`;
}

export const SCHEDULE_LIMITS = {
  minDurationWeeks: 1,
  maxDurationWeeks: 52,
} as const;
